import { HttpError } from "@microbeta/common-frontend";
import config from "../../config";
import { httpClient } from "../utils/httpClientInstance";

export interface AreaStatistics {
  area_id: string;
  route_count: number;
  min_grade: number | null;
  max_grade: number | null;
  grade_distribution: { [grade: number]: number };
}

export interface GlobalStatistics {
  total_areas: number;
  total_models: number;
  total_routes: number;
  grade_range: {
    min_grade: number | null;
    max_grade: number | null;
  };
}

export interface BulkAreaStatistics {
  [area_id: string]: AreaStatistics;
}

const BASE_URL = `${config.VITE_CONTENT_SERVICE_URL}/content/statistics`;

/**
 * Get global platform statistics
 */
export const getGlobalStatistics = async (): Promise<GlobalStatistics> => {
  try {
    const result = await httpClient.get<GlobalStatistics>(`${BASE_URL}/global`);
    return result;
  } catch (error: unknown) {
    console.error("Error fetching global statistics:", error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to fetch global statistics: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(
      "An unknown error occurred while fetching global statistics."
    );
  }
};

/**
 * Get statistics for a specific area
 */
export const getAreaStatistics = async (
  areaId: string
): Promise<AreaStatistics> => {
  if (!areaId) throw new Error("area ID is required.");

  try {
    const result = await httpClient.get<AreaStatistics>(
      `${BASE_URL}/areas/${areaId}`
    );
    return result;
  } catch (error: unknown) {
    console.error(`Error fetching area statistics for ${areaId}:`, error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to fetch area statistics: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(
      `An unknown error occurred while fetching area statistics for ${areaId}.`
    );
  }
};

/**
 * Get statistics for multiple areas at once (bulk operation)
 */
export const getBulkAreaStatistics = async (
  areaIds: string[]
): Promise<BulkAreaStatistics> => {
  if (!Array.isArray(areaIds) || areaIds.length === 0) {
    return {};
  }

  if (areaIds.length > 100) {
    throw new Error(
      "Cannot request statistics for more than 100 areas at once"
    );
  }

  try {
    const result = await httpClient.post<BulkAreaStatistics>(
      `${BASE_URL}/areas/bulk`,
      {
        area_ids: areaIds,
      }
    );
    return result;
  } catch (error: unknown) {
    console.error("Error fetching bulk area statistics:", error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to fetch bulk area statistics: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(
      "An unknown error occurred while fetching bulk area statistics."
    );
  }
};
